import { clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';
import { router } from '@inertiajs/svelte';
import { ClockArrowDown, ClockArrowUp, ArrowUpAZ, ArrowDownZA } from 'lucide-svelte';

const twMerge = extendTailwindMerge({
  // Per tailwind-merge docs, config extensions must go under `extend`.
  // We add our custom font-size utilities so they don't conflict with text color utilities.
  // Ref: https://github.com/dcastil/tailwind-merge/blob/main/docs/api-reference.md
  extend: {
    classGroups: {
      'font-size': ['text-smaller', 'text-xxs'],
    },
  },
})

export function cn(...inputs) {
	return twMerge(clsx(inputs));
}

export function titleCase(string) {
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
}

export function formatMoney(cents) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(cents / 100)
}

export function initials(name) {
  if (!name) return '';
  return name
    .split(' ')
    .map(n => n[0]?.toUpperCase() || '')
    .join('');
}

export function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

export function delay(ms = 100) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export function formatDuration(seconds) {
  return new Date(seconds * 1000).toISOString().substr(11, 8)
}

export function formatObject(object) {
  if (!object || typeof object !== 'object') return ''
  return Object.entries(object).map(([key, value]) => `${key}: ${value}`).join(", ")
}

export function mergePath(path, params = {}, { replace = false } = {}) {
  path ||= window.location
  const url = new URL(path)

  if (replace) {
    url.search = "" // wipe existing params
  }

  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === "") {
      url.searchParams.delete(key) // remove param
    } else {
      url.searchParams.set(key, value) // add/update param
    }
  })

  return url.toString()
}

export function setParam(key, value, options = {}) {
  router.visit(mergePath(null, { [key]: value }), options)
}

export function setParams(params = {}, options = {}) {
  router.visit(mergePath(null, params), options)
}

export function getParam(key, defaultValue = null) {
  const url = new URL(window.location.href)
  return url.searchParams.get(key) || defaultValue
}

export function clearParams({ keys = [], except = [] } = {}) {
  const url = new URL(window.location.href)
  if (keys.length > 0) {
    keys.forEach(key => url.searchParams.delete(key))
  } else if (except.length > 0) {
    Array.from(url.searchParams.keys()).forEach(key => {
      if (!except.includes(key)) {
        url.searchParams.delete(key)
      }
    })
  } else {
    url.search = ""
  }
  router.visit(url.toString())
}

export function hasParams(keys = []) {
  const url = new URL(window.location.href)
  // If no keys are provided, return true if there are any params
  if (keys.length === 0) {
    return url.searchParams.size > 0
  } else {
    return keys.some(key => url.searchParams.has(key))
  }
}

export function formatDetails(value) {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'object') return JSON.stringify(value, null, 2)
  return String(value)
}

export const sortItems = [
  { value: "newest", label: "Most recent", icon: ClockArrowDown },
  { value: "oldest", label: "Oldest", icon: ClockArrowUp },
  { value: "az", label: "Alphabetical A-Z", icon: ArrowUpAZ },
  { value: "za", label: "Alphabetical Z-A", icon: ArrowDownZA }
]